<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">Countries</h1>
					<p class="text-sm text-gray-500 mt-1 mb-0">Manage country settings and delivery options</p>
				</div>
				<button
					type="button"
					class="inline-flex items-center gap-2 bg-orange-7 hover:bg-orange-6 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors"
					@click="openCreateModal"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
					</svg>
					Create New
				</button>
			</div>
		</header>

		<main class="p-6">
			<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<div class="py-4 px-6 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
					<div class="flex items-center gap-2">
						<h2 class="text-base font-semibold text-gray-700 m-0">Countries</h2>
						<span class="bg-orange-2 text-orange-7 py-0.5 px-2 rounded-full text-xs font-medium">
							{{ filteredCountries.length }} total
						</span>
					</div>
					<div class="flex items-center gap-4 flex-wrap">
						<div class="relative">
							<select
								v-model="selectedRegionId"
								class="py-2 px-3 pr-8 border border-gray-300 rounded-md text-sm text-gray-700 bg-white cursor-pointer appearance-none min-w-[150px]"
							>
								<option :value="null">All Regions</option>
								<option v-for="region in regions" :key="region.id" :value="region.id">
									{{ region.name }}
								</option>
							</select>
							<svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
							</svg>
						</div>
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search by name or ISO..."
								class="py-2 px-3 pl-9 border border-gray-300 rounded-md text-sm w-[220px]"
							>
						</div>
						<button
							class="inline-flex items-center gap-1.5 py-2 px-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer transition-colors"
							@click="refreshData"
						>
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
							Refresh
						</button>
					</div>
				</div>

				<div v-if="loading" class="flex justify-center items-center py-16">
					<div class="text-center">
						<div class="w-10 h-10 border-[3px] border-gray-200 border-t-orange-7 rounded-full animate-spin mx-auto"></div>
						<p class="mt-4 text-gray-500 text-sm">Loading countries...</p>
					</div>
				</div>

				<div v-else-if="paginatedCountries.length === 0" class="text-center py-16">
					<svg class="w-12 h-12 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<p class="mt-4 text-gray-500 text-sm">No countries found</p>
				</div>

				<div v-else class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr class="bg-gray-50">
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Flag</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ISO</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ISO3</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Region</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Currency</th>
								<th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">States</th>
								<th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Delivery</th>
								<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="country in paginatedCountries"
								:key="country.id"
								class="border-t border-gray-200 transition-colors hover:bg-gray-50"
							>
								<td class="py-4 px-6">
									<country-flag :country="country.iso" size="normal" />
								</td>
								<td class="py-4 px-6">
									<span class="text-sm font-medium text-gray-900">{{ country.name }}</span>
								</td>
								<td class="py-4 px-6">
									<span class="text-sm text-gray-500">{{ country.iso }}</span>
								</td>
								<td class="py-4 px-6">
									<span class="text-sm text-gray-500">{{ country.iso3 }}</span>
								</td>
								<td class="py-4 px-6">
									<span class="text-sm text-gray-500">{{ getRegionName(country.regionid) }}</span>
								</td>
								<td class="py-4 px-6">
									<span class="text-sm text-gray-500">{{ country.currency || '-' }}</span>
								</td>
								<td class="py-4 px-6 text-center">
									<span
										v-if="country.states"
										class="inline-flex items-center gap-1 py-0.5 px-2 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
									>
										<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
											<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
										</svg>
										Yes
									</span>
									<span v-else class="text-xs text-gray-400">No</span>
								</td>
								<td class="py-4 px-6 text-center">
									<span
										v-if="country.delivery"
										class="inline-flex items-center gap-1 py-0.5 px-2 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
									>
										<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
											<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
										</svg>
										Yes
									</span>
									<span v-else class="text-xs text-gray-400">No</span>
								</td>
								<td class="py-4 px-6 text-right">
									<div class="flex items-center justify-end gap-1">
										<button
											class="p-1.5 bg-transparent border-none text-orange-7 cursor-pointer rounded transition-colors hover:bg-orange-2"
											title="Edit country"
											@click="editCountry(country)"
										>
											<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
											</svg>
										</button>
										<button
											class="p-1.5 bg-transparent border-none text-red-500 cursor-pointer rounded transition-colors hover:bg-red-50"
											title="Delete country"
											@click="confirmDeleteCountry(country)"
										>
											<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex justify-between items-center">
					<div class="text-sm text-gray-500">
						Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} countries
					</div>
					<div class="flex items-center gap-2">
						<select
							v-model="pageSize"
							class="py-1.5 px-2 border border-gray-300 rounded text-sm text-gray-700"
							@change="currentPage = 1"
						>
							<option :value="10">10 per page</option>
							<option :value="25">25 per page</option>
							<option :value="50">50 per page</option>
							<option :value="100">100 per page</option>
						</select>
						<button
							:disabled="currentPage === 1"
							class="py-1.5 px-3 border border-gray-300 rounded text-sm cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
							@click="currentPage > 1 ? currentPage-- : null"
						>Previous</button>
						<span class="text-sm text-gray-700">Page {{ currentPage }} of {{ totalPages }}</span>
						<button
							:disabled="currentPage === totalPages"
							class="py-1.5 px-3 border border-gray-300 rounded text-sm cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
							@click="currentPage < totalPages ? currentPage++ : null"
						>Next</button>
					</div>
				</div>
			</div>
		</main>

		<div v-if="showCreateModal" class="fixed inset-0 z-50">
			<div class="fixed inset-0 bg-black/50" @click="closeCreateModal"></div>
			<div class="fixed inset-0 overflow-y-auto">
				<div class="flex min-h-full items-center justify-center p-4">
					<div class="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl relative z-[51]">
						<h3 class="text-xl font-semibold text-gray-900 m-0">Create New Country</h3>
						<p class="mt-1 text-sm text-gray-500">Add a new country to the system.</p>
						<div class="mt-6">
							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
								<input
									v-model="newCountry.name"
									type="text"
									placeholder="Enter country name"
									class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
								>
							</div>
							<div class="grid grid-cols-2 gap-4 mb-4">
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-2">ISO Code *</label>
									<input
										v-model="newCountry.iso"
										type="text"
										placeholder="US"
										maxlength="2"
										class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border uppercase"
									>
								</div>
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-2">ISO3 Code *</label>
									<input
										v-model="newCountry.iso3"
										type="text"
										placeholder="USA"
										maxlength="3"
										class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border uppercase"
									>
								</div>
							</div>
							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-2">Region</label>
								<select
									v-model="newCountry.regionid"
									class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
								>
									<option :value="null">Select region</option>
									<option v-for="region in regions" :key="region.id" :value="region.id">
										{{ region.name }}
									</option>
								</select>
							</div>
						</div>
						<div class="mt-6 flex flex-col gap-3">
							<button
								type="button"
								:disabled="!newCountry.name?.trim() || !newCountry.iso?.trim() || !newCountry.iso3?.trim() || saving"
								class="w-full py-2.5 px-4 text-sm font-medium text-white bg-orange-7 hover:bg-orange-6 border-none rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								@click="createCountry"
							>
								{{ saving ? 'Creating...' : 'Create Country' }}
							</button>
							<button
								type="button"
								class="w-full py-2.5 px-4 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer transition-colors"
								@click="closeCreateModal"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import CountryService from 'services/CountryService';
import RegionService from 'services/RegionService';
import { showDeleteConfirmation } from 'utils/swal';
import Swal from 'sweetalert2';

const showToast = (message, icon = 'success') => {
	Swal.fire({
		toast: true,
		position: 'top-end',
		icon,
		title: message,
		showConfirmButton: false,
		timer: 3000,
		timerProgressBar: true,
		customClass: {
			popup: 'windmill-swal-toast',
		},
	});
};

export default defineComponent({
	name: 'CountriesPage',
	setup() {
		const router = useRouter();
		const countries = ref([]);
		const regions = ref([]);
		const loading = ref(false);
		const saving = ref(false);
		const showCreateModal = ref(false);
		const searchQuery = ref('');
		const selectedRegionId = ref(null);
		const currentPage = ref(1);
		const pageSize = ref(10);

		const newCountry = ref({
			name: '',
			iso: '',
			iso3: '',
			regionid: null,
		});

		const filteredCountries = computed(() => {
			let result = countries.value;
			if (selectedRegionId.value) {
				result = result.filter(c => c.regionid === selectedRegionId.value);
			}
			if (searchQuery.value) {
				const query = searchQuery.value.toLowerCase();
				result = result.filter(c =>
					c.name?.toLowerCase().includes(query) ||
					c.iso?.toLowerCase().includes(query) ||
					c.iso3?.toLowerCase().includes(query)
				);
			}
			return result;
		});

		const totalRows = computed(() => filteredCountries.value.length);
		const totalPages = computed(() => Math.ceil(totalRows.value / pageSize.value) || 1);

		const paginatedCountries = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredCountries.value.slice(start, start + pageSize.value);
		});

		const startRow = computed(() => {
			if (totalRows.value === 0) return 0;
			return (currentPage.value - 1) * pageSize.value + 1;
		});

		const endRow = computed(() => {
			return Math.min(currentPage.value * pageSize.value, totalRows.value);
		});

		const getRegionName = (regionid) => {
			if (!regionid) return '-';
			const region = regions.value.find(r => r.id === regionid);
			return region ? region.name : '-';
		};

		const fetchCountries = async () => {
			loading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				countries.value = await CountryService.getAll(params);
			} catch (error) {
				showToast(error.message || 'Failed to load countries', 'error');
			} finally {
				loading.value = false;
			}
		};

		const fetchRegions = async () => {
			try {
				const params = new URLSearchParams({ limit: '0' });
				regions.value = await RegionService.getAll(params);
			} catch (error) {
				console.error('Failed to load regions:', error);
			}
		};

		const refreshData = () => {
			fetchCountries();
		};

		const openCreateModal = () => {
			newCountry.value = { name: '', iso: '', iso3: '', regionid: null };
			showCreateModal.value = true;
		};

		const closeCreateModal = () => {
			showCreateModal.value = false;
		};

		const createCountry = async () => {
			if (!newCountry.value.name?.trim() || !newCountry.value.iso?.trim() || !newCountry.value.iso3?.trim()) return;
			saving.value = true;
			try {
				const created = await CountryService.create({
					name: newCountry.value.name.trim(),
					iso: newCountry.value.iso.trim().toUpperCase(),
					iso3: newCountry.value.iso3.trim().toUpperCase(),
					regionid: newCountry.value.regionid,
				});
				countries.value.unshift(created);
				closeCreateModal();
				showToast('Country created successfully');
			} catch (error) {
				showToast(error.message || 'Failed to create country', 'error');
			} finally {
				saving.value = false;
			}
		};

		const editCountry = (country) => {
			router.push({ name: 'edit-country', params: { id: country.id } });
		};

		const confirmDeleteCountry = async (country) => {
			const result = await showDeleteConfirmation(
				'Delete Country',
				`Are you sure you want to delete "${country.name}"? This action cannot be undone.`
			);
			if (result.isConfirmed) {
				try {
					await CountryService.delete(country.id);
					countries.value = countries.value.filter(c => c.id !== country.id);
					showToast('Country deleted successfully');
				} catch (error) {
					showToast(error.message || 'Failed to delete country', 'error');
				}
			}
		};

		onMounted(() => {
			fetchRegions();
			fetchCountries();
		});

		return {
			countries,
			regions,
			loading,
			saving,
			showCreateModal,
			searchQuery,
			selectedRegionId,
			currentPage,
			pageSize,
			newCountry,
			filteredCountries,
			paginatedCountries,
			totalRows,
			totalPages,
			startRow,
			endRow,
			getRegionName,
			refreshData,
			openCreateModal,
			closeCreateModal,
			createCountry,
			editCountry,
			confirmDeleteCountry,
		};
	},
});
</script>

<style scoped>
@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
</style>
